<template>
  <div
    v-if="visible"
    class="tab-context-menu"
    :style="{ top: y + 'px', left: x + 'px' }"
    @click.stop
  >
    <button class="tab-context-menu__item" :disabled="isCurrentPinned" @click="closeTab">
      닫기
    </button>
    <button class="tab-context-menu__item" @click="closeOthers">
      다른 탭 모두 닫기
    </button>
    <button class="tab-context-menu__item" @click="closeRight">
      오른쪽 탭 닫기
    </button>
    <button class="tab-context-menu__item" @click="closeLeft">
      왼쪽 탭 닫기
    </button>
    <div class="tab-context-menu__divider"></div>
    <button class="tab-context-menu__item" @click="togglePin">
      {{ isCurrentPinned ? '고정 해제' : '탭 고정' }}
    </button>
  </div>
</template>

<script>
import { useTabStore } from '@/stores/tabs'

/**
 * @component TabContextMenu
 * @description 탭 우클릭 시 표시되는 컨텍스트 메뉴 컴포넌트.
 * 탭 닫기, 다른 탭 닫기, 오른쪽/왼쪽 탭 닫기, 탭 고정/해제 기능을 제공합니다.
 * x, y 좌표를 받아 fixed 위치에 렌더링됩니다.
 */
export default {
  name: 'TabContextMenu',
  // ── Props ──
  props: {
    /**
     * 컨텍스트 메뉴 표시 여부
     * @type {boolean}
     */
    visible: { type: Boolean, default: false },
    /**
     * 메뉴 표시 X 좌표 (clientX)
     * @type {number}
     */
    x: { type: Number, default: 0 },
    /**
     * 메뉴 표시 Y 좌표 (clientY)
     * @type {number}
     */
    y: { type: Number, default: 0 },
    /**
     * 우클릭된 탭의 고유 ID
     * @type {string}
     */
    tabId: { type: String, default: '' },
    /**
     * 전체 탭 목록
     * @type {Array}
     */
    tabs: { type: Array, default: () => [] }
  },
  emits: ['close', 'navigate'],
  setup() {
    const tabStore = useTabStore()
    return { tabStore }
  },
  // ── 계산된 속성 ──
  computed: {
    /**
     * 현재 우클릭된 탭 객체를 반환합니다.
     */
    currentTab() {
      return this.tabs.find((t) => t.id === this.tabId)
    },
    /**
     * 현재 탭이 고정(pin)된 상태인지 여부를 반환합니다.
     */
    isCurrentPinned() {
      return this.currentTab?.pinned || false
    }
  },
  // ── 메서드 ──
  methods: {
    /**
     * 현재 탭을 닫고 다음 활성 탭으로 이동합니다. 고정 탭은 닫지 않습니다.
     */
    closeTab() {
      if (this.isCurrentPinned) return
      const nextPath = this.tabStore.closeTab(this.tabId)
      if (nextPath) this.$emit('navigate', nextPath)
      this.$emit('close')
    },
    /**
     * 현재 탭을 제외한 나머지 탭을 모두 닫습니다.
     */
    closeOthers() {
      this.tabStore.closeOtherTabs(this.tabId)
      this.tabStore.setActive(this.tabId)
      const tab = this.tabs.find((t) => t.id === this.tabId)
      if (tab) this.$emit('navigate', tab.fullPath)
      this.$emit('close')
    },
    /**
     * 현재 탭 오른쪽의 모든 탭을 닫습니다.
     */
    closeRight() {
      this.tabStore.closeRightTabs(this.tabId)
      this.$emit('close')
    },
    /**
     * 현재 탭 왼쪽의 모든 탭을 닫습니다.
     */
    closeLeft() {
      this.tabStore.closeLeftTabs(this.tabId)
      this.$emit('close')
    },
    /**
     * 현재 탭의 고정/해제 상태를 토글합니다.
     */
    togglePin() {
      if (this.isCurrentPinned) {
        this.tabStore.unpinTab(this.tabId)
      } else {
        this.tabStore.pinTab(this.tabId)
      }
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
@media (min-width: 1280px) {
  /* --- 컨텍스트 메뉴 패널 (fixed 위치, 클릭 좌표 기준) --- */
  .tab-context-menu {
    position: fixed;
    z-index: var(--z-dropdown, 200);
    min-width: 160px;
    background: var(--color-white, #FFFFFF);
    border: 1px solid var(--color-gray-100, #E8EAED);
    border-radius: var(--radius-md, 8px);
    box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
    padding: var(--space-1, 4px) 0;
  }

  .tab-context-menu__item {
    display: block;
    width: 100%;
    padding: 8px 16px;
    border: none;
    background: transparent;
    color: var(--color-gray-600, #424750);
    font-size: var(--font-body-sm, 0.8125rem);
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
    transition: background var(--transition-fast, 0.15s ease);
  }

  .tab-context-menu__item:hover {
    background: var(--color-primary-50, #E8EDF4);
  }

  .tab-context-menu__item:disabled {
    color: var(--color-gray-300, #A0A5AE);
    cursor: not-allowed;
  }

  .tab-context-menu__item:disabled:hover {
    background: transparent;
  }

  .tab-context-menu__divider {
    height: 1px;
    margin: var(--space-1, 4px) 0;
    background: var(--color-gray-100, #E8EAED);
  }
}
</style>
